import { DeleteOutlined, DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Flex, Popconfirm, Table, Tooltip, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { EditableText, editableTypes } from "../../../common/components/EditableText";
import {
  FilePreviewModal,
  type PreviewableFile,
  useFilePreview,
} from "../../../common/components/FilePreviewModal";
import { FilePreviewTrigger } from "../../../common/components/FilePreviewTrigger";
import { FileThumbnail } from "../../../common/components/FileThumbnail";
import { apiUrl } from "../../../common/utils/apiUrl";
import { canDeleteFile } from "../../../common/utils/canDeleteFile";
import { formatFileSize } from "../../../common/utils/formatFileSize";
import { deleteFile, updateFileRelation } from "../../files/store";
import { downloadAllTicketFiles } from "../../files/zip-export/store";
import type {
  FileRelationsDetailed,
  ModelFile,
  TicketItemDetailed,
} from "../../generated-axios-client";
import type { RootState } from "../../store";
import { TicketFileDropzone } from "./TicketFileDropzone";

type FilesInfoProps = {
  intl: IntlShape;
  files: ModelFile[] | null;
  dispatch: Dispatch;
  ticketId: string;
  items?: TicketItemDetailed[];
  fileRelations?: FileRelationsDetailed[];
};

export function buildDownloadUrl(file: { id?: string }): string {
  const token = localStorage.getItem("access") ?? "";
  return apiUrl(
    `api/v1/management/files/${file.id}/download/?token=${encodeURIComponent(token)}`
  );
}

export function buildThumbnailUrl(file: { id?: string }): string {
  const token = localStorage.getItem("access") ?? "";
  return apiUrl(
    `api/v1/management/files/${file.id}/thumbnail/?token=${encodeURIComponent(token)}`
  );
}

export const FileNameCell = ({
  file,
  intl,
  onPreview,
}: {
  file: ModelFile;
  intl: IntlShape;
  onPreview: (file: PreviewableFile) => void;
}) => (
  <FilePreviewTrigger
    onPreview={() => onPreview(file)}
    ariaLabel={`${intl.formatMessage({ id: "ticket_file_preview" })}: ${file.name ?? ""}`}
  >
    <FileThumbnail
      src={file.id ? buildThumbnailUrl(file) : undefined}
      contentType={file.content_type}
      name={file.name}
    />
    <Typography.Text ellipsis={{ tooltip: file.name }}>{file.name}</Typography.Text>
  </FilePreviewTrigger>
);

export const FileActionsCell = ({
  file,
  canDelete = false,
  intl,
  dispatch,
  onPreview,
}: {
  file: ModelFile;
  canDelete?: boolean;
  intl: IntlShape;
  dispatch: Dispatch;
  onPreview: (file: PreviewableFile) => void;
}) => (
  <Flex gap={4} justify="flex-end">
    <Tooltip title={intl.formatMessage({ id: "ticket_file_preview" })} placement="top">
      <Button
        type="text"
        size="small"
        icon={<EyeOutlined />}
        onClick={() => onPreview(file)}
      />
    </Tooltip>
    <Tooltip title={intl.formatMessage({ id: "ticket_file_download" })} placement="top">
      <Typography.Link href={buildDownloadUrl(file)} download={file.name ?? true}>
        <Button type="text" size="small" icon={<DownloadOutlined />} />
      </Typography.Link>
    </Tooltip>
    {canDelete && (
      <Popconfirm
        title={intl.formatMessage({ id: "ticket_file_delete_confirm_title" })}
        description={intl.formatMessage({ id: "ticket_file_delete_confirm_content" })}
        onConfirm={() => file.id && dispatch(deleteFile(file.id))}
      >
        <Tooltip title={intl.formatMessage({ id: "ticket_file_delete" })} placement="top">
          <Button type="text" size="small" danger icon={<DeleteOutlined />} />
        </Tooltip>
      </Popconfirm>
    )}
  </Flex>
);

export const FilesInfo = ({
  intl,
  files,
  dispatch,
  ticketId,
  items,
  fileRelations,
}: FilesInfoProps) => {
  const filePreview = useFilePreview(files ?? []);
  const { openPreview } = filePreview;
  const currentUser = useSelector((state: RootState) => state.userstate.user);

  const filePathToUploadedBy = useMemo(() => {
    const map: Record<string, number | null> = {};
    for (const fr of fileRelations ?? []) {
      const filePath = fr.file?.file_path;
      if (filePath) {
        map[filePath] = fr.uploaded_by ?? null;
      }
    }
    return map;
  }, [fileRelations]);

  const itemLabelById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of items ?? []) {
      if (!item.id) continue;
      map[item.id] = item.ticket_item_number != null ? `#${item.ticket_item_number}` : "";
    }
    return map;
  }, [items]);

  const filePathToTask = useMemo(() => {
    const map: Record<string, string> = {};
    for (const fr of fileRelations ?? []) {
      const filePath = fr.file?.file_path;
      if (filePath && fr.ticket_item) {
        map[filePath] = itemLabelById[fr.ticket_item] ?? "";
      }
    }
    return map;
  }, [fileRelations, itemLabelById]);

  const filePathToRelationId = useMemo(() => {
    const map: Record<string, string> = {};
    for (const fr of fileRelations ?? []) {
      const filePath = fr.file?.file_path;
      if (filePath && fr.id) {
        map[filePath] = fr.id;
      }
    }
    return map;
  }, [fileRelations]);

  const filePathToDescription = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const fr of fileRelations ?? []) {
      const filePath = fr.file?.file_path;
      if (filePath) {
        map[filePath] = fr.description ?? null;
      }
    }
    return map;
  }, [fileRelations]);

  const columns: ColumnType<ModelFile>[] = [
    {
      key: "name",
      title: intl.formatMessage({ id: "ticket_file_name" }),
      render: (_: unknown, file: ModelFile) => (
        <FileNameCell file={file} intl={intl} onPreview={openPreview} />
      ),
    },
    {
      key: "task",
      title: intl.formatMessage({ id: "ticket_task_file_column" }),
      width: 130,
      render: (_: unknown, file: ModelFile) => {
        const taskLabel = file.file_path ? filePathToTask[file.file_path] : undefined;
        return taskLabel ? (
          <Typography.Text type="secondary">{taskLabel}</Typography.Text>
        ) : null;
      },
    },
    {
      key: "description",
      title: intl.formatMessage({ id: "ticket_file_description" }),
      render: (_: unknown, file: ModelFile) => {
        const relationId = file.file_path
          ? filePathToRelationId[file.file_path]
          : undefined;
        const description = file.file_path
          ? filePathToDescription[file.file_path]
          : undefined;
        return (
          <EditableText
            variableName="description"
            variableValue={description ?? undefined}
            intl={intl}
            dataType={editableTypes.TEXT}
            onChange={
              relationId
                ? (_name, value) =>
                    dispatch(
                      updateFileRelation({ relationId, description: value || null })
                    )
                : undefined
            }
            maxLength={255}
          />
        );
      },
    },
    {
      key: "size",
      dataIndex: "size",
      title: intl.formatMessage({ id: "ticket_file_size" }),
      width: 100,
      render: (size: number) => (
        <Typography.Text type="secondary">{formatFileSize(size)}</Typography.Text>
      ),
    },
    {
      key: "actions",
      width: 116,
      render: (_: unknown, file: ModelFile) => (
        <FileActionsCell
          file={file}
          canDelete={canDeleteFile({
            uploadedBy: file.file_path ? filePathToUploadedBy[file.file_path] : undefined,
            currentUserId: currentUser?.id,
            currentUserGroups: currentUser?.groups,
          })}
          intl={intl}
          dispatch={dispatch}
          onPreview={openPreview}
        />
      ),
    },
  ];

  return (
    <Flex vertical gap="middle">
      <TicketFileDropzone intl={intl} dispatch={dispatch} ticketId={ticketId} />
      {files && files.length > 0 && (
        <>
          <Flex justify="flex-end">
            <Button
              icon={<DownloadOutlined />}
              onClick={() => dispatch(downloadAllTicketFiles({ files }))}
            >
              {intl.formatMessage({ id: "ticket_files_download_all" })}
            </Button>
          </Flex>
          <Table
            columns={columns}
            dataSource={files}
            rowKey={(f) => f.id ?? f.file_path ?? ""}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
          />
        </>
      )}
      <FilePreviewModal
        preview={filePreview}
        getDownloadUrl={buildDownloadUrl}
        intl={intl}
      />
    </Flex>
  );
};
